import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import AppHeader from "@/components/AppHeader";
import { motion, AnimatePresence } from "framer-motion";
import { Gift, Link2, Loader2, ArrowRight, Sparkles, AlertCircle } from "lucide-react";
import { useCurrency } from "@/hooks/useCurrency";

interface ProductMetadata {
  title: string | null;
  description: string | null;
  image: string | null;
  price: number | null;
  currency: string | null;
  url: string;
  blocked: boolean;
  source: string;
}

const GiftMe = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const currency = useCurrency();
  const [url, setUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const [product, setProduct] = useState<ProductMetadata | null>(null);
  const [error, setError] = useState<string | null>(null);

  const extractUrl = (input: string): string | null => {
    const match = input.match(/https?:\/\/[^\s"'<>]+/i);
    return match ? match[0] : null;
  };

  const handleExtract = async () => {
    if (!url.trim()) return;
    const cleanUrl = extractUrl(url.trim());
    if (!cleanUrl) {
      setError(t("giftMe.extractError"));
      return;
    }
    setLoading(true);
    setError(null);
    setProduct(null);

    const maxAttempts = 2;
    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      try {
        const { data, error: fnError } = await supabase.functions.invoke("extract-url-metadata", {
          body: { url: cleanUrl },
        });

        if (fnError) throw fnError;

        if (!data?.title && !data?.image && !data?.price) {
          if (attempt < maxAttempts - 1) {
            await new Promise((r) => setTimeout(r, 1000));
            continue;
          }
          setError(t("giftMe.noMetadata"));
          setLoading(false);
          return;
        }

        setProduct(data as ProductMetadata);
        setLoading(false);
        return;
      } catch {
        if (attempt < maxAttempts - 1) {
          await new Promise((r) => setTimeout(r, 1000));
          continue;
        }
        setError(t("giftMe.extractError"));
      }
    }
    setLoading(false);
  };

  const handleContinue = () => {
    if (!product) return;
    const params = new URLSearchParams();
    if (product.title) params.set("productName", product.title);
    if (product.price) params.set("productPrice", String(product.price));
    if (product.currency) params.set("productCurrency", product.currency);
    if (product.image) params.set("productImage", product.image);
    if (product.url) params.set("productUrl", product.url);
    params.set("giftMe", "1");
    navigate(`/create?${params.toString()}`);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && url.trim()) handleExtract();
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <AppHeader />

      <div className="flex-1 flex flex-col items-center justify-center px-4 pb-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-md space-y-8 text-center"
        >
          {/* Hero */}
          <div className="space-y-3">
            <div className="mx-auto w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center">
              <Gift className="w-8 h-8 text-primary" />
            </div>
            <h1 className="text-3xl font-bold text-foreground font-serif">
              {t("giftMe.title")}
            </h1>
            <p className="text-muted-foreground text-sm leading-relaxed max-w-sm mx-auto">
              {t("giftMe.subtitle")}
            </p>
          </div>

          {/* URL Input */}
          <div className="space-y-3">
            <div className="relative">
              <Link2 className="absolute start-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={t("giftMe.placeholder")}
                className="ps-10 border-border h-12 text-sm"
                disabled={loading}
              />
            </div>
            <Button
              onClick={handleExtract}
              disabled={!url.trim() || loading}
              size="lg"
              className="w-full rounded-full bg-primary text-primary-foreground hover:bg-primary/90"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 me-2 animate-spin" />
                  {t("giftMe.extracting")}
                </>
              ) : (
                <>
                  <Sparkles className="w-4 h-4 me-2" />
                  {t("giftMe.extract")}
                </>
              )}
            </Button>
          </div>

          {/* Error */}
          <AnimatePresence>
            {error && (
              <motion.div
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                className="flex items-center gap-2 text-sm text-destructive bg-destructive/10 rounded-xl px-4 py-3"
              >
                <AlertCircle className="w-4 h-4 shrink-0" />
                {error}
              </motion.div>
            )}
          </AnimatePresence>

          {/* Product Card */}
          <AnimatePresence>
            {product && (
              <motion.div
                initial={{ opacity: 0, scale: 0.95, y: 12 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95 }}
                className="bg-card border border-border rounded-2xl overflow-hidden shadow-lg text-start"
              >
                {product.image && (
                  <div className="w-full aspect-video bg-muted overflow-hidden">
                    <img
                      src={product.image}
                      alt={product.title || "Product"}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        (e.target as HTMLImageElement).style.display = "none";
                      }}
                    />
                  </div>
                )}
                <div className="p-5 space-y-3">
                  {product.title && (
                    <h3 className="text-base font-semibold text-foreground leading-snug line-clamp-2">
                      {product.title}
                    </h3>
                  )}
                  {product.description && (
                    <p className="text-sm text-muted-foreground line-clamp-2">
                      {product.description}
                    </p>
                  )}
                  {product.price && (
                    <p className="text-lg font-bold text-primary">
                      {product.currency || currency} {product.price.toLocaleString()}
                    </p>
                  )}

                  <Button
                    onClick={handleContinue}
                    size="lg"
                    className="w-full rounded-full bg-primary text-primary-foreground hover:bg-primary/90 mt-2"
                  >
                    {t("giftMe.continue")}
                    <ArrowRight className="w-4 h-4 ms-2" />
                  </Button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* How it works mini */}
          {!product && !loading && (
            <div className="pt-4 space-y-4">
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                {t("giftMe.howTitle")}
              </p>
              <div className="grid gap-3 text-start">
                {[
                  { step: "1", text: t("giftMe.how1") },
                  { step: "2", text: t("giftMe.how2") },
                  { step: "3", text: t("giftMe.how3") },
                ].map((item) => (
                  <div key={item.step} className="flex items-start gap-3">
                    <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/10 text-primary text-xs font-bold flex items-center justify-center">
                      {item.step}
                    </span>
                    <p className="text-sm text-muted-foreground leading-relaxed">{item.text}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </motion.div>
      </div>
    </div>
  );
};

export default GiftMe;
